import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Layout, Header } from 'app/theme/layout'
import ImageHero from 'app/theme/ImageHero'
import NavbarFixed from 'app/theme/NavbarFixed'
import { OccasionPageGrid, OccasionPageGridRow } from 'app/occasions/shared/OccasionPageGrid'
import heroGuarantee from 'assets/img/roaming-hunger-guarantee.jpg'
import heroGuaranteeIcon from 'assets/svg/guarantee.svg'
import introSausageTrukSmoke from 'assets/svg/sausage-truck-smoke.svg'
import introLock from 'assets/svg/lock.svg'
import introBurger from 'assets/svg/burger.svg'
import introForkKnife from 'assets/svg/fork-knife.svg'
import introDocument from 'assets/svg/document.svg'
import styles from './GuaranteePage.module.css'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'

export default function GuaranteePage() {
  const { openModal, ...modalState } = useModalState()

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>The 100% Roaming Hunger Guarantee - Roaming Hunger</title>
        <meta
          name="description"
          content="With all the planning you have to do, catering should be checked off the list as soon as you talk to us. While it’s rare for a caterer to not be able to service an event, after almost a decade of e..."
        />
        <meta name="og:title" content="The 100% Roaming Hunger Guarantee" />
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <ImageHero
        imageUrl={heroGuarantee}
        onButtonClick={openModal}
        title={(
          <div>
            <img
              src={heroGuaranteeIcon}
              className={[styles.hero_icon, 'align-middle mx-auto']}
            />
            The 100% Roaming Hunger Guarantee
          </div>
        )}
        description="We guarantee every catering, every time. Because food just tastes better when you have peace of mind."
      />
      <NavbarFixed menuItems={navMenu} onButtonClick={openModal} />
      <Intro />
      <IntroGrid />
    </Layout>
  )
}

const navMenu = [
  {
    title: 'The 100% Roaming Hunger Guarantee',
    url: '/food-truck-catering/roaming-hunger-guarantee/',
  },
  {
    title: 'Frequently Asked Questions',
    url: '/food-truck-catering/guarantee-faq/#submenu',
  },
]

function Intro() {
  return (
    <div className="container text-center mt-12 px-8">
      <h2 className="text-navy text-4xl font-platform leading-tight">
        Get yourself a huge helping of peace of mind.
      </h2>
      <p className="text-gray-900 font-light mt-6">
        With all the planning you have to do, catering should be checked off the
        list as soon as you talk to us. While it’s rare for a caterer to not be
        able to service an event, after almost a decade of experience we
        understand it can happen. The 100% Roaming Hunger Guarantee will
        reimburse you beyond just getting your money back. You’ll get 100% of
        what you paid for the catering.
      </p>
    </div>
  )
}

function IntroGrid() {
  return (
    <OccasionPageGrid>
      <OccasionPageGridRow
        heading="Every Catering. Every time."
        content="Your event is extremely important. Our job is to make sure the experience is awesome, no matter how many guests you’re expecting. Every catering is backed by our 100% Roaming Hunger Guarantee at no additional cost to you, so you can enjoy your meal."
        image={introSausageTrukSmoke}
        imageClassName="w-3/4 h-64 md:mt-10 md:mb-10"
        reverse
      />
      <OccasionPageGridRow
        heading="Unrivaled Protection"
        content="Our work is done only when you feel full. That’s why we’re giving you an unprecedented level of protection. Unexpected surprises shouldn’t be your problem, they’re ours."
        image={introLock}
        imageClassName="w-3/4 h-64 mt-10 mb-10"
      />
      <OccasionPageGridRow
        heading="How You’re Protected"
        content=""
        image={introBurger}
        imageClassName="w-3/4 h-64 mt-10 mb-10"
        reverse
      >
        <p>
          The Roaming Hunger Guarantee provides protection in case of incidents
          of non-service by one of our catering partners. Refunds and
          reimbursements are subject to certain conditions and limitations. For
          full details, please read the{' '}
          <a
            href="https://roaminghunger.com/food-truck-catering/guarantee-faq/#submenu"
            target="_blank"
            rel="noopener"
            className="text-blue"
          >
            100% Roaming Hunger Guarantee FAQ page
          </a>
          .
        </p>
        <p></p>
        <p>
          The 100% Roaming Hunger Guarantee: If on the day of your catering the
          scheduled caterer fails to appear at your event without 24 hours’
          prior notice to you or your Roaming Hunger Agent, and we cannot find a
          suitable replacement, you will be entitled to a full refund.
        </p>
        <p></p>
      </OccasionPageGridRow>
      <OccasionPageGridRow
        heading="What’s Not Protected"
        content=""
        image={introForkKnife}
        imageClassName="w-3/4 h-64 mt-10 mb-10"
      >
        <p>
          The Roaming Hunger Guarantee does not apply in certain cases, such as:
        </p>
        <p>
          - The host (you) fails to secure proper permissions or permits for the
          food truck to operate at the catering location.
        </p>
        <p>
          - The event is cancelled or delayed due to unforeseen reasons such as
          weather or other incidents outside of the caterer’s control.
        </p>
        <p>
          - You fail to provide a proper space for the food truck to operate
          safely and securely.
        </p>
        <p>
          Other incidents that occur during your event, such as failure by your
          caterer to provide an item that was paid for, are outside the limits
          of the 100% Roaming Hunger Guarantee but covered under the{' '}
          <a
            href="https://roaminghunger.com/food-truck-catering/catering-terms/"
            target="_blank"
            rel="noopener"
            className="text-blue"
          >
            Catering Services Agreement
          </a>
          .
        </p>
        <p></p>
      </OccasionPageGridRow>
      <OccasionPageGridRow
        heading="Need Even More Peace of Mind?"
        content=""
        image={introDocument}
        imageClassName="w-3/4 h-64 mt-10 mb-10"
        reverse
      >
        <p>
          Roaming Hunger makes sure that you are always protected against
          property damage, to the tune of $1,000,000. If the catering service
          you booked through Roaming Hunger ever results in damage to the
          building or property where the catering took place, contact us
          immediately. For more information on how you are protected, please
          read our{' '}
          <a
            href="https://roaminghunger.com/food-truck-catering/catering-terms/"
            className="text-blue"
          >
            terms and conditions
          </a>
          .
        </p>

        <p>
          If you ever have any issues or questions, before, during, or after
          your event, please contact your Account Manager directly or call us at
          1-800-568-9370.
        </p>
      </OccasionPageGridRow>
    </OccasionPageGrid>
  )
}
